import {
    IonAccordion,
    IonAccordionGroup,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonNote,
    IonSegment,
    IonSegmentButton,
    IonToggle
} from '@ionic/react';
import {
    arrowBackOutline,
    arrowForwardOutline,
    moonOutline,
    notificationsOffOutline,
    notificationsOutline,
    warningOutline
} from 'ionicons/icons';
import type { ReactNode } from 'react';
import {
    PushNotificationConfig,
    PushNotificationConfigJson,
    PushNotificationRuleKey,
    PushNotificationRuleKeys,
    PushNotificationRuleValue,
    PushNotificationRuleValues
} from '../models/PushNotificationConfig';
import { useTranslation } from '../hooks/useTranslation';
import { useSetPushNotificationConfigMutation } from '../query/pushNotificationConfigQuery';
import useUX from '../utils/ux';
import TransitDirectionIcon from './TransitDirectionIcon';
import styles from './PushNotificationSettingsForm.module.css';

interface PushNotificationSettingsFormProps {
    config: PushNotificationConfig;
}

interface SectionProps {
    title: string;
    description: string;
    children: ReactNode;
}

interface RuleValueSegmentProps {
    value: PushNotificationRuleValue;
    disabled?: boolean;
    onChange: (value: PushNotificationRuleValue) => void;
}

const defaultStartTime = '22:00';
const defaultEndTime = '07:00';

function getLocalTimezone(fallback: string) {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || fallback;
    } catch {
        return fallback;
    }
}

function normalizeTime(value: string | undefined, fallback: string) {
    const match = /^(\d{2}):?(\d{2})$/.exec(value ?? '');

    if (!match) {
        return fallback;
    }

    return `${match[1]}:${match[2]}`;
}

function parseTimeRange(timeRange: string) {
    const [start, end] = timeRange.split('-');

    return {
        start: normalizeTime(start, defaultStartTime),
        end: normalizeTime(end, defaultEndTime)
    };
}

const Section: React.FC<SectionProps> = ({ title, description, children }) => {
    return (
        <section className={styles.section}>
            <div className={styles.sectionTitleRow}>
                <h2 className={styles.sectionTitle}>{title}</h2>
            </div>
            <p className={styles.sectionDescription}>{description}</p>
            {children}
        </section>
    );
};

const RuleValueSegment: React.FC<RuleValueSegmentProps> = ({ value, disabled, onChange }) => {
    const { t } = useTranslation('modals');

    return (
        <IonSegment mode="ios"
            className={styles.ruleSegment}
            value={value}
            disabled={disabled}
            onIonChange={(event) => {
                if (event.detail.value) {
                    onChange(event.detail.value as PushNotificationRuleValue);
                }
            }}
        >
            <IonSegmentButton value={PushNotificationRuleValues.OFF}>
                <IonIcon icon={notificationsOffOutline} />
                <IonLabel>{t('push_notification_settings.options.off')}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value={PushNotificationRuleValues.ON}>
                <IonIcon icon={notificationsOutline} />
                <IonLabel>{t('push_notification_settings.options.on')}</IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value={PushNotificationRuleValues.CRITICAL}>
                <IonIcon icon={warningOutline} />
                <IonLabel>{t('push_notification_settings.options.critical')}</IonLabel>
            </IonSegmentButton>
        </IonSegment>
    );
};

const PushNotificationSettingsForm: React.FC<PushNotificationSettingsFormProps> = ({ config }) => {
    const { t } = useTranslation('modals');
    const { presentResult } = useUX();
    const setConfigMutation = useSetPushNotificationConfigMutation();
    const quietHoursRange = parseTimeRange(config.quietHours.timeRange);
    const controlsDisabled = setConfigMutation.isPending || !config.pushEnabled;

    const saveConfig = (updates: Partial<PushNotificationConfigJson>) => {
        const nextConfig = new PushNotificationConfig({
            ...config.toConfigJson(),
            ...updates
        });

        void presentResult(() => setConfigMutation.mutateAsync(nextConfig.toConfigJson()));
    };

    const setRule = (ruleKey: PushNotificationRuleKey, value: PushNotificationRuleValue) => {
        saveConfig({
            rules: {
                ...config.rules,
                [ruleKey]: value
            }
        });
    };

    const setToggleRule = (ruleKey: PushNotificationRuleKey, checked: boolean) => {
        setRule(ruleKey, checked ? PushNotificationRuleValues.ON : PushNotificationRuleValues.OFF);
    };

    const setQuietHours = (updates: Partial<PushNotificationConfig['quietHours']>) => {
        saveConfig({
            quietHours: {
                ...config.quietHours,
                ...updates
            }
        });
    };

    const getRuleHelp = (value: PushNotificationRuleValue) => {
        switch (value) {
            case PushNotificationRuleValues.CRITICAL:
                return t('push_notification_settings.critical_notification_help');
            case PushNotificationRuleValues.ON:
                return t('push_notification_settings.standard_notification_help');
            default:
                return t('push_notification_settings.off_notification_help');
        }
    };

    return (
        <div className={styles.wrapper}>
            <IonList className={styles.listCard}>
                <IonItem lines="none">
                    <IonIcon slot="start" icon={notificationsOutline} color="primary" />
                    <IonLabel>
                        <h2>{t('push_notification_settings.push_notifications')}</h2>
                    </IonLabel>
                    <IonToggle
                        slot="end"
                        checked={config.pushEnabled}
                        disabled={setConfigMutation.isPending}
                        aria-label={t('push_notification_settings.push_notifications')}
                        onIonChange={(event) => saveConfig({ pushEnabled: event.detail.checked })}
                    />
                </IonItem>
            </IonList>

            <Section
                title={t('push_notification_settings.contraband')}
                description={t('push_notification_settings.contraband_description')}
            >
                <IonList className={styles.listCard}>
                    <IonItem lines="none" className={styles.segmentItem}>
                        <RuleValueSegment
                            value={config.rules[PushNotificationRuleKeys.CONTRABAND]}
                            disabled={controlsDisabled}
                            onChange={(value) => setRule(PushNotificationRuleKeys.CONTRABAND, value)}
                        />
                    </IonItem>
                    <IonNote color="medium" className={styles.ruleHelp}>
                        {getRuleHelp(config.rules[PushNotificationRuleKeys.CONTRABAND])}
                    </IonNote>
                </IonList>
            </Section>

            <Section
                title={t('push_notification_settings.transit_events')}
                description={t('push_notification_settings.transit_events_description')}
            >
                <IonList className={styles.listCard}>
                    <IonItem>
                        <TransitDirectionIcon slot="start" direction="inward" />
                        <IonLabel>
                            <h2>{t('push_notification_settings.transit_inward')}</h2>
                            <p>{t('push_notification_settings.transit_inward_description')}</p>
                        </IonLabel>
                        <IonToggle
                            slot="end"
                            checked={config.rules[PushNotificationRuleKeys.TRANSIT_INWARD] !== PushNotificationRuleValues.OFF}
                            disabled={controlsDisabled}
                            aria-label={t('push_notification_settings.transit_inward')}
                            onIonChange={(event) => setToggleRule(PushNotificationRuleKeys.TRANSIT_INWARD, event.detail.checked)}
                        />
                    </IonItem>
                    <IonItem>
                        <TransitDirectionIcon slot="start" direction="outward" />
                        <IonLabel>
                            <h2>{t('push_notification_settings.transit_outward')}</h2>
                            <p>{t('push_notification_settings.transit_outward_description')}</p>
                        </IonLabel>
                        <IonToggle
                            slot="end"
                            checked={config.rules[PushNotificationRuleKeys.TRANSIT_OUTWARD] !== PushNotificationRuleValues.OFF}
                            disabled={controlsDisabled}
                            aria-label={t('push_notification_settings.transit_outward')}
                            onIonChange={(event) => setToggleRule(PushNotificationRuleKeys.TRANSIT_OUTWARD, event.detail.checked)}
                        />
                    </IonItem>
                </IonList>
            </Section>

            <Section
                title={t('push_notification_settings.peek_events')}
                description={t('push_notification_settings.peek_events_description')}
            >
                <IonList className={styles.listCard}>
                    <IonItem>
                        <TransitDirectionIcon slot="start" direction="peek-inward" />
                        <IonLabel>
                            <h2>{t('push_notification_settings.peek_inward')}</h2>
                            <p>{t('push_notification_settings.peek_inward_description')}</p>
                        </IonLabel>
                        <IonToggle
                            slot="end"
                            checked={config.rules[PushNotificationRuleKeys.PEEK_INWARD] !== PushNotificationRuleValues.OFF}
                            disabled={controlsDisabled}
                            aria-label={t('push_notification_settings.peek_inward')}
                            onIonChange={(event) => setToggleRule(PushNotificationRuleKeys.PEEK_INWARD, event.detail.checked)}
                        />
                    </IonItem>
                    <IonItem>
                        <TransitDirectionIcon slot="start" direction="peek-outward" />
                        <IonLabel>
                            <h2>{t('push_notification_settings.peek_outward')}</h2>
                            <p>{t('push_notification_settings.peek_outward_description')}</p>
                        </IonLabel>
                        <IonToggle
                            slot="end"
                            checked={config.rules[PushNotificationRuleKeys.PEEK_OUTWARD] !== PushNotificationRuleValues.OFF}
                            disabled={controlsDisabled}
                            aria-label={t('push_notification_settings.peek_outward')}
                            onIonChange={(event) => setToggleRule(PushNotificationRuleKeys.PEEK_OUTWARD, event.detail.checked)}
                        />
                    </IonItem>
                </IonList>
            </Section>

            <Section
                title={t('push_notification_settings.deny_events')}
                description={t('push_notification_settings.deny_events_description')}
            >
                <IonList className={styles.listCard}>
                    <IonItem>
                        <TransitDirectionIcon slot="start" direction="deny-inward" />
                        <IonLabel>
                            <h2>{t('push_notification_settings.deny_inward')}</h2>
                            <p>{t('push_notification_settings.deny_inward_description')}</p>
                        </IonLabel>
                        <IonToggle
                            slot="end"
                            checked={config.rules[PushNotificationRuleKeys.DENY_INWARD] !== PushNotificationRuleValues.OFF}
                            disabled={controlsDisabled}
                            aria-label={t('push_notification_settings.deny_inward')}
                            onIonChange={(event) => setToggleRule(PushNotificationRuleKeys.DENY_INWARD, event.detail.checked)}
                        />
                    </IonItem>
                    <IonItem>
                        <TransitDirectionIcon slot="start" direction="deny-outward" />
                        <IonLabel>
                            <h2>{t('push_notification_settings.deny_outward')}</h2>
                            <p>{t('push_notification_settings.deny_outward_description')}</p>
                        </IonLabel>
                        <IonToggle
                            slot="end"
                            checked={config.rules[PushNotificationRuleKeys.DENY_OUTWARD] !== PushNotificationRuleValues.OFF}
                            disabled={controlsDisabled}
                            aria-label={t('push_notification_settings.deny_outward')}
                            onIonChange={(event) => setToggleRule(PushNotificationRuleKeys.DENY_OUTWARD, event.detail.checked)}
                        />
                    </IonItem>
                </IonList>
            </Section>

            <Section
                title={t('push_notification_settings.lock_breaches')}
                description={t('push_notification_settings.lock_breaches_description')}
            >
                <IonList className={styles.listCard}>
                    <IonItem>
                        <IonIcon slot="start" icon={arrowBackOutline} color="danger" />
                        <IonLabel>
                            <h2>{t('push_notification_settings.forced_entry')}</h2>
                            <p>{t('push_notification_settings.forced_entry_description')}</p>
                        </IonLabel>
                    </IonItem>
                    <IonItem lines="none" className={styles.segmentItem}>
                        <RuleValueSegment
                            value={config.rules[PushNotificationRuleKeys.BREACH_INWARD]}
                            disabled={controlsDisabled}
                            onChange={(value) => setRule(PushNotificationRuleKeys.BREACH_INWARD, value)}
                        />
                    </IonItem>
                    <IonItem>
                        <IonIcon slot="start" icon={arrowForwardOutline} color="danger" />
                        <IonLabel>
                            <h2>{t('push_notification_settings.forced_exit')}</h2>
                            <p>{t('push_notification_settings.forced_exit_description')}</p>
                        </IonLabel>
                    </IonItem>
                    <IonItem lines="none" className={styles.segmentItem}>
                        <RuleValueSegment
                            value={config.rules[PushNotificationRuleKeys.BREACH_OUTWARD]}
                            disabled={controlsDisabled}
                            onChange={(value) => setRule(PushNotificationRuleKeys.BREACH_OUTWARD, value)}
                        />
                    </IonItem>
                </IonList>
            </Section>

            <IonList className={`${styles.listCard} ${styles.quietHoursCard}`}>
                <IonAccordionGroup>
                    <IonAccordion value="quiet-hours">
                        <IonItem slot="header">
                            <IonIcon slot="start" icon={moonOutline} color="medium" />
                            <IonLabel>
                                <h2>{t('push_notification_settings.quiet_hours')}</h2>
                                <p>
                                    {config.quietHours.enabled
                                        ? t('push_notification_settings.quiet_hours_on_summary', {
                                            timeRange: config.quietHours.timeRange
                                        })
                                        : t('push_notification_settings.quiet_hours_off_summary')}
                                </p>
                            </IonLabel>
                        </IonItem>
                        <div slot="content" className={styles.quietHoursContent}>
                            <IonItem>
                                <IonToggle
                                    checked={config.quietHours.enabled}
                                    disabled={setConfigMutation.isPending || !config.pushEnabled}
                                    onIonChange={(event) => setQuietHours({
                                        enabled: event.detail.checked,
                                        timezone: event.detail.checked && !config.quietHours.enabled
                                            ? getLocalTimezone(config.quietHours.timezone)
                                            : config.quietHours.timezone
                                    })}
                                >
                                    {t('push_notification_settings.quiet_hours_enabled')}
                                </IonToggle>
                            </IonItem>
                            <IonNote color="medium" className={styles.ruleHelp}>
                                {t('push_notification_settings.quiet_hours_description')}
                            </IonNote>
                            <IonItem>
                                <IonInput
                                    type="time"
                                    label={t('push_notification_settings.start_time')}
                                    labelPlacement="stacked"
                                    value={quietHoursRange.start}
                                    disabled={setConfigMutation.isPending || !config.pushEnabled || !config.quietHours.enabled}
                                    onIonChange={(event) => {
                                        const value = event.detail.value;
                                        if (typeof value === 'string') {
                                            setQuietHours({ timeRange: `${value}-${quietHoursRange.end}` });
                                        }
                                    }}
                                />
                            </IonItem>
                            <IonItem>
                                <IonInput
                                    type="time"
                                    label={t('push_notification_settings.end_time')}
                                    labelPlacement="stacked"
                                    value={quietHoursRange.end}
                                    disabled={setConfigMutation.isPending || !config.pushEnabled || !config.quietHours.enabled}
                                    onIonChange={(event) => {
                                        const value = event.detail.value;
                                        if (typeof value === 'string') {
                                            setQuietHours({ timeRange: `${quietHoursRange.start}-${value}` });
                                        }
                                    }}
                                />
                            </IonItem>
                            <IonItem lines="none">
                                <IonToggle
                                    checked={config.quietHours.criticalBypasses}
                                    disabled={setConfigMutation.isPending || !config.pushEnabled || !config.quietHours.enabled}
                                    onIonChange={(event) => setQuietHours({ criticalBypasses: event.detail.checked })}
                                >
                                    {t('push_notification_settings.critical_bypasses')}
                                </IonToggle>
                            </IonItem>
                        </div>
                    </IonAccordion>
                </IonAccordionGroup>
            </IonList>
        </div>
    );
};

export default PushNotificationSettingsForm;
